'use client';
/**
 * LES EXAMENS QUE L'APPAREIL PEUT FAIRE — et, devant chacun, s'il a son CQI
 * (Ines, 29/10/2026 : « rajouter un onglet examen qui permet de relier la liste
 * des analyses que peut faire la machine. Et en fait, on retire les CQI. Devant
 * chaque examen, on voit si un CQI est déjà associé »).
 *
 * ── Le circuit, qui existe déjà ─────────────────────────────────────────────
 *
 * appareil ↔ TECHNIQUE (`technique.equipements`) ↔ EXAMEN (`technique.fiches`).
 * Un examen ne se relie donc pas « à la machine » en direct : il passe par la
 * technique qu'elle met en œuvre — c'est ce que lit le dossier de méthode, et ce
 * que l'impact d'une sortie de service utilise déjà. Cet onglet ne crée aucun
 * second lien : il écrit par la route du référentiel des techniques
 * (`POST /ged/manuel-ref/techniques/:id`, RQ), la même que l'écran du catalogue.
 *
 * ── Le CQI devant chaque examen ─────────────────────────────────────────────
 *
 * Une carte CQI porte un PARAMÈTRE (analyte) et un appareil. Un examen a son CQI
 * sur cet appareil si une carte en cours de l'appareil porte l'un de ses
 * paramètres (`/ged/manuel-ref/parametres` donne, pour chaque paramètre, ses
 * examens). Rien n'est stocké : tout se recalcule à la lecture.
 *
 * ── Réactifs et lots (spec 002, récit 5, EF-030) ───────────────────────────
 *
 * Listés, plus seulement comptés : les produits du catalogue dont c'est
 * l'appareil par défaut (`equipementCode`, `GET ged/produits`) et les lots
 * reçus pour lui (registre des lots du magasin). Ils se relient depuis la
 * Présentation (« Relier des réactifs ») ou à la réception d'un lot.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { Pastille, Selecteur, inputStyle, ConfirmButton, I, enChargement } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { Tableau } from '@/shared/Tableau';
import { fmtDate, LOT_STATES } from '@/lib/meta';
import type { GedEquipement } from '@/lib/types';
import { lbl } from './commun';

type Exam = { documentId: string; code: string; libelle: string; archivee?: boolean };
type Technique = {
  documentId: string; code: string; nom: string; actif: boolean;
  fiches: Exam[]; equipements: { documentId: string; code: string; nom: string }[];
};
type Parametre = { code: string; nom: string; examens?: { code: string }[] };
type Carte = { carteId: string; analyte: string; analyteNom: string; statut: string };
type Produit = { code: string; libelle: string; famille: string; equipementCode: string; actif: boolean; fournisseurNom?: string };
const FAMILLES_PRODUIT: Record<string, string> = { reactif: 'Réactif', calibrant: 'Calibrant', controle: 'Matériau de contrôle', consommable: 'Consommable' };

async function lireJson<T>(chemin: string): Promise<T | null> {
  try { const r = await fetch(`/api/ged/${chemin}`, { credentials: 'include' }); return r.ok ? (await r.json()) as T : null; } catch { return null; }
}

export function ExamensEquipement({ e }: { e: GedEquipement }) {
  const { session, lots, booted, loading } = useGed();
  const peutGerer = session.roles.includes('rq');
  const [techniques, setTechniques] = React.useState<Technique[] | null>(null);
  const [parametres, setParametres] = React.useState<Parametre[] | null>(null);
  const [cartes, setCartes] = React.useState<Carte[] | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [tour, setTour] = React.useState(0);
  const [ajoutTech, setAjoutTech] = React.useState('');
  const [ouvreTech, setOuvreTech] = React.useState(false);
  const [ajoutExam, setAjoutExam] = React.useState<{ tech: string; exam: string } | null>(null);
  const [message, setMessage] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  const [produits, setProduits] = React.useState<Produit[] | null>(null);
  const [erreurProduits, setErreurProduits] = React.useState('');

  React.useEffect(() => {
    let vivant = true;
    void lireJson<{ lignes: Produit[] }>('ged/produits').then((j) => {
      if (!vivant) return;
      if (!j) { setErreurProduits('Le catalogue des produits n’a pas pu être lu.'); return; }
      setErreurProduits('');
      setProduits((j.lignes || []).filter((p) => p.equipementCode === e.code));
    });
    return () => { vivant = false; };
  }, [e.code]);
  const lotsAppareil = React.useMemo(
    () => (lots || []).filter((l) => !!e.code && l.equipementCode === e.code)
      .sort((a, b) => String(b.dateReception || '').localeCompare(String(a.dateReception || ''))),
    [lots, e.code],
  );
  const lotsEnChargement = enChargement(booted, loading, (lots || []).length);

  React.useEffect(() => {
    let vivant = true;
    void Promise.all([
      lireJson<{ techniques: Technique[] }>('ged/manuel-ref/techniques'),
      lireJson<{ parametres: Parametre[] }>('ged/manuel-ref/parametres'),
      lireJson<{ cartes?: Carte[]; resume?: Carte[] } | Carte[]>(`ged/cqi-cartes?equipement=${encodeURIComponent(e.documentId)}`),
    ]).then(([t, p, c]) => {
      if (!vivant) return;
      if (!t) { setErreur('Le référentiel des techniques n’a pas pu être lu.'); return; }
      setErreur('');
      setTechniques(t.techniques || []);
      setParametres(p?.parametres || []);
      const lc = Array.isArray(c) ? c : (c?.cartes || c?.resume || []);
      setCartes(lc);
    });
    return () => { vivant = false; };
  }, [e.documentId, tour]);

  /* Les examens couverts par une carte CQI EN COURS de l'appareil. */
  const cqiParExamen = React.useMemo(() => {
    const m = new Map<string, string[]>();
    if (!cartes || !parametres) return m;
    const examensDe = new Map(parametres.map((p) => [p.code, (p.examens || []).map((x) => x.code)]));
    for (const c of cartes) {
      for (const ex of examensDe.get(c.analyte) || []) m.set(ex, [...(m.get(ex) || []), c.analyteNom || c.analyte]);
    }
    return m;
  }, [cartes, parametres]);

  if (erreur) return <p style={{ margin: 0, fontSize: 12.5, color: 'var(--amber-600)' }}>{erreur}</p>;
  if (!techniques || !parametres || !cartes) return <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture des examens de l’appareil…</p>;

  const siennes = techniques.filter((t) => t.equipements.some((x) => x.documentId === e.documentId));
  const autres = techniques.filter((t) => t.actif && !siennes.includes(t));
  const examens = new Map<string, Exam>();
  for (const t of techniques) for (const f of t.fiches) if (!f.archivee) examens.set(f.code, f);
  const catalogue = [...examens.values()].sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr'));
  const nbExamens = new Set(siennes.flatMap((t) => t.fiches.filter((f) => !f.archivee).map((f) => f.code))).size;
  const sansCqi = new Set(siennes.flatMap((t) => t.fiches.filter((f) => !f.archivee && !cqiParExamen.has(f.code)).map((f) => f.code))).size;

  const ecrire = async (t: Technique, corps: Record<string, unknown>, ok: string) => {
    setEnCours(true); setMessage('');
    const r = await fetchEcriture(`/api/ged/ged/manuel-ref/techniques/${encodeURIComponent(t.documentId)}`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps),
    });
    const d = await r.json().catch(() => null);
    setEnCours(false);
    if (!r.ok) { setMessage(d?.error?.message || 'Modification refusée.'); return false; }
    setMessage(ok); setAjoutTech(''); setAjoutExam(null);
    setTour((x) => x + 1);
    return true;
  };
  /* `equipementIds` REMPLACE la liste : on renvoie donc celle de la technique,
     augmentée ou privée de CET appareil — sans toucher aux autres. */
  const avecAppareil = (t: Technique, garder: boolean) => ({
    equipementIds: [...t.equipements.map((x) => x.documentId).filter((id) => id !== e.documentId), ...(garder ? [e.documentId] : [])],
  });

  /* ── MISE EN PAGE (Ines, 03/11/2026 : « pas du tout intuitive », « je ne
     vois pas comment rajouter de nouvelles techniques », « le lien vers les
     CQI est totalement inutile »). L'ajout d'une technique était un sélecteur
     en BAS de page, après toutes les techniques : il passe en tête, en bouton.
     Chaque technique porte ses actions dans son en-tête (ajouter un examen,
     détacher) ; chaque examen, son « Retirer ». Le renvoi vers l'écran CQI est
     retiré : la pastille devant chaque examen dit déjà ce qu'il faut savoir. */
  const nbSansCqiTxt = nbExamens ? `${sansCqi} sans CQI` : '';
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Pastille ton="muted" compacte>{siennes.length} technique{siennes.length > 1 ? 's' : ''}</Pastille>
        <Pastille ton="muted" compacte>{nbExamens} examen{nbExamens > 1 ? 's' : ''}</Pastille>
        {nbExamens > 0 && <Pastille ton={sansCqi ? 'warning' : 'success'} compacte>{sansCqi ? nbSansCqiTxt : 'tous avec CQI'}</Pastille>}
        <span style={{ flex: 1 }} />
        {peutGerer && !ouvreTech && (
          <Bouton taille="sm" variante="primaire" onClick={() => { setOuvreTech(true); setAjoutTech(''); setMessage(''); }}>
            <I n="plus" s={12} /> Ajouter une technique
          </Bouton>
        )}
      </div>

      {peutGerer && ouvreTech && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)' }}>
          <div>
            <label style={lbl} htmlFor={`tech-ajout-${e.documentId}`}>Technique mise en œuvre par cet appareil</label>
            <Selecteur id={`tech-ajout-${e.documentId}`} value={ajoutTech} onChange={(v) => { setAjoutTech(v); setMessage(''); }} style={{ ...inputStyle, minWidth: 320 }}>
              <option value="">Choisir une technique…</option>
              {autres.map((t) => <option key={t.documentId} value={t.documentId}>{t.nom} — {t.fiches.filter((f) => !f.archivee).length} examen(s)</option>)}
            </Selecteur>
          </div>
          <Bouton taille="sm" onClick={() => setOuvreTech(false)} disabled={enCours}>Annuler</Bouton>
          <Bouton taille="sm" variante="primaire" disabled={!ajoutTech || enCours}
            onClick={() => { const t = autres.find((x) => x.documentId === ajoutTech); if (t) void ecrire(t, avecAppareil(t, true), `Technique « ${t.nom} » ajoutée : ses examens apparaissent ci-dessous.`).then((fait) => { if (fait) setOuvreTech(false); }); }}>
            Ajouter
          </Bouton>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flexBasis: '100%' }}>
            Une technique absente de la liste se crée dans le catalogue des analyses.
          </span>
        </div>
      )}
      {!!message && <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>{message}</p>}

      {!siennes.length && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
            <strong style={{ color: 'var(--text-body)' }}>Aucune technique</strong> ne déclare cet appareil : ni examen ni
            paramètre ne lui est relié.
            {peutGerer ? ' Reliez-le à la technique qu’il met en œuvre : ses examens apparaîtront ici.' : ''}
          </p>
          {peutGerer && !ouvreTech && (
            <Bouton taille="sm" onClick={() => { setOuvreTech(true); setAjoutTech(''); setMessage(''); }}>Relier à une technique</Bouton>
          )}
        </div>
      )}

      {siennes.map((t) => {
        const actifs = t.fiches.filter((f) => !f.archivee).sort((a, b2) => a.libelle.localeCompare(b2.libelle, 'fr'));
        const ajoutIci = ajoutExam?.tech === t.documentId;
        return (
          <section key={t.documentId} style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '10px 12px', display: 'grid', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 13 }}>{t.nom}</strong>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{t.code}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>· {actifs.length} examen{actifs.length > 1 ? 's' : ''}</span>
              {!t.actif && <Pastille ton="muted" compacte>technique inactive</Pastille>}
              <span style={{ flex: 1 }} />
              {peutGerer && !ajoutIci && (
                <Bouton taille="sm" onClick={() => { setAjoutExam({ tech: t.documentId, exam: '' }); setMessage(''); }}>
                  <I n="plus" s={12} /> Examen
                </Bouton>
              )}
              {peutGerer && (
                <ConfirmButton label="Détacher la technique" variant="outline"
                  question={`Retirer cet appareil de la technique « ${t.nom} » ? Ses examens ne lui seront plus reliés. La technique et ses examens restent au catalogue.`}
                  onConfirm={() => ecrire(t, avecAppareil(t, false), `Technique « ${t.nom} » détachée de l’appareil.`)} />
              )}
            </div>
            {peutGerer && ajoutIci && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Selecteur id={`ex-${t.documentId}`} aria-label={`Examen à ajouter à ${t.nom}`} value={ajoutExam.exam} onChange={(v) => setAjoutExam({ tech: t.documentId, exam: v })} style={{ ...inputStyle, maxWidth: 380 }}>
                  <option value="">Choisir un examen du catalogue…</option>
                  {catalogue.filter((x) => !t.fiches.some((f) => f.code === x.code)).map((x) => <option key={x.documentId} value={x.documentId}>{x.libelle} ({x.code})</option>)}
                </Selecteur>
                <Bouton taille="sm" onClick={() => setAjoutExam(null)} disabled={enCours}>Annuler</Bouton>
                <Bouton taille="sm" variante="primaire" disabled={!ajoutExam.exam || enCours}
                  onClick={() => void ecrire(t, { ficheDocumentId: ajoutExam.exam }, `Examen ajouté à « ${t.nom} ».`)}>Ajouter</Bouton>
              </div>
            )}
            {!actifs.length ? (
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun examen n’emprunte encore cette technique.</span>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid' }}>
                {actifs.map((f) => {
                  const cqi = cqiParExamen.get(f.code);
                  return (
                    <li key={f.documentId} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '5px 0', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5 }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-muted)', minWidth: 70 }}>{f.code}</span>
                      <span style={{ flex: 1, color: 'var(--text-body)' }}>{f.libelle}</span>
                      {cqi
                        ? <Pastille ton="success" compacte title={`Carte(s) CQI de l’appareil : ${cqi.join(', ')}`}>CQI associé</Pastille>
                        : <Pastille ton="warning" compacte title="Aucune carte CQI en cours sur cet appareil ne porte un paramètre de cet examen.">pas de CQI</Pastille>}
                      {/* Retirer un examen ajouté par erreur (Ines, 03/11/2026) : le
                          serveur savait détacher (`detacherFicheDocumentId`), l'écran
                          n'offrait que l'ajout. L'examen est relié à la TECHNIQUE,
                          pas à l'appareil : la question nomme les autres appareils
                          qui le perdent aussi. */}
                      {peutGerer && (
                        <ConfirmButton label="Retirer" variant="outline"
                          question={`Retirer l’examen « ${f.libelle} » de la technique « ${t.nom} » ?`
                            + (t.equipements.length > 1
                              ? ` Il ne sera plus relié non plus à : ${t.equipements.filter((x) => x.documentId !== e.documentId).map((x) => `${x.code} — ${x.nom}`).join(', ')}.`
                              : '')
                            + ' L’examen reste au catalogue.'}
                          onConfirm={() => ecrire(t, { detacherFicheDocumentId: f.documentId }, `Examen retiré de « ${t.nom} ».`)} />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}

      <section aria-label="Réactifs de l’appareil" style={{ display: 'grid', gap: 6, marginTop: 6 }}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Réactifs, calibrants et contrôles</h4>
        <Tableau
          id="examens-appareil-reactifs"
          niveau="simple"
          ajusterLargeur
          lignes={produits || []}
          chargement={produits === null && !erreurProduits}
          erreur={erreurProduits || null}
          cleLigne={(p) => p.code}
          vide={{ titre: 'Aucun produit du catalogue n’a cet appareil pour appareil par défaut.', indice: '« Relier des réactifs », dans la Présentation, les choisit dans le catalogue.' }}
          colonnes={[
            { cle: 'libelle', libelle: 'Produit', valeur: (p) => p.libelle, style: { whiteSpace: 'normal' }, rendu: (p) => p.libelle },
            { cle: 'famille', libelle: 'Famille', type: 'liste', valeur: (p) => FAMILLES_PRODUIT[p.famille] || p.famille, rendu: (p) => FAMILLES_PRODUIT[p.famille] || p.famille },
            { cle: 'fournisseur', libelle: 'Fournisseur', valeur: (p) => p.fournisseurNom || '', rendu: (p) => p.fournisseurNom || '' },
            { cle: 'actif', libelle: 'État', type: 'liste', valeur: (p) => (p.actif ? 'actif' : 'inactif'),
              rendu: (p) => (p.actif ? <Pastille ton="success" compacte>actif</Pastille> : <Pastille ton="muted" compacte>inactif</Pastille>) },
          ]}
        />
      </section>

      <section aria-label="Lots de l’appareil" style={{ display: 'grid', gap: 6 }}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Lots reçus pour cet appareil</h4>
        <Tableau
          id="examens-appareil-lots"
          niveau="simple"
          ajusterLargeur
          lignes={lotsAppareil}
          chargement={lotsEnChargement}
          cleLigne={(l) => l.documentId}
          vide={{ titre: 'Aucun lot n’est enregistré pour cet appareil.', indice: 'Le lot reçu se rattache à l’appareil à sa réception (registre des lots).' }}
          colonnes={[
            { cle: 'designation', libelle: 'Désignation', valeur: (l) => l.designation, style: { whiteSpace: 'normal' }, rendu: (l) => l.designation },
            { cle: 'numeroLot', libelle: 'N° de lot', valeur: (l) => l.numeroLot, rendu: (l) => <span style={{ fontFamily: 'var(--font-mono)' }}>{l.numeroLot}</span> },
            { cle: 'reception', libelle: 'Reçu le', type: 'date', valeur: (l) => l.dateReception || '', rendu: (l) => fmtDate(l.dateReception) },
            { cle: 'peremption', libelle: 'Péremption', type: 'date', valeur: (l) => l.datePeremption || '', rendu: (l) => fmtDate(l.datePeremption) },
            { cle: 'statut', libelle: 'Statut', type: 'liste', valeur: (l) => LOT_STATES[l.statut]?.label || l.statut,
              rendu: (l) => <Pastille ton={(LOT_STATES[l.statut]?.tone || 'neutral') as React.ComponentProps<typeof Pastille>['ton']} compacte>{LOT_STATES[l.statut]?.label || l.statut}</Pastille> },
          ]}
        />
      </section>
    </div>
  );
}
